import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { SettingsFormFooter, SettingsOptionList } from '@/containers/Settings/SettingsLayout'
import API from '@/lib/api'
import {
  type OwnerManageableFeature,
  ownerManageableFeatures,
  toggleFeatureKeys,
} from '@/lib/features/featureList'

const schema = z.object({
  features: z.array(z.enum(ownerManageableFeatures)),
})

type AccountFeaturesFormData = z.infer<typeof schema>

export function AccountFeaturesForm({
  enabledFeatures,
  onSuccess,
}: {
  enabledFeatures: string[]
  onSuccess: () => void
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings.account.features',
  })
  const { t: ts } = useTranslation(undefined, {
    keyPrefix: 'sidebar',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const form = useForm<AccountFeaturesFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      features: ownerManageableFeatures.filter((feature) =>
        enabledFeatures.includes(feature),
      ),
    },
  })

  const onSubmit = (data: AccountFeaturesFormData) =>
    API.patch('/api/v2/settings/features', {
      features: data.features,
    })
      .then(() => {
        toast.success(t('success'))
        onSuccess()
      })
      .catch(() => {
        toast.error(t('error'))
      })

  const featureLabels: Record<OwnerManageableFeature, string> = {
    cashRegistry: ts('cashRegister'),
    catalog: ts('catalog'),
    warehouse: ts('warehouse'),
    eshop: ts('eshop'),
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="features"
          render={({ field }) => (
            <SettingsOptionList>
              {ownerManageableFeatures.map((feature) => (
                <FormItem key={feature} className="flex flex-row items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0 space-y-0.5">
                    <FormLabel className="text-sm font-medium">{featureLabels[feature]}</FormLabel>
                    <FormDescription>{t(`items.${feature}.description`)}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value.includes(feature)}
                      onCheckedChange={(checked) => {
                        field.onChange(toggleFeatureKeys(field.value, feature, checked === true))
                      }}
                    />
                  </FormControl>
                </FormItem>
              ))}
            </SettingsOptionList>
          )}
        />

        <SettingsFormFooter hint="Některé moduly potřebují katalog, ten se zapne s nimi.">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {tg('button.save')}
          </Button>
        </SettingsFormFooter>
      </form>
    </Form>
  )
}
